// frontend/src/components/shared/CaseShareModal.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Copy,
  Check,
  Share2,
  Mail,
  ExternalLink,
  MessageCircle,
  FileText,
  User,
  MapPin,
  Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { logActivity } from '../../api';
import { useApp } from '../../context/AppContext';

export const CaseShareModal = ({ open, onOpenChange, caseData }) => {
  const { currentUserId, currentUser } = useApp();
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedSummary, setCopiedSummary] = useState(false);
  const [targetPhone, setTargetPhone] = useState('');

  if (!caseData) return null;

  const origin = window.location.origin;
  const caseUrl = `${origin}/cases/${caseData.id}`;
  const clientName = caseData.client?.name || caseData.clientName || 'N/A';
  const productName = caseData.product?.name || caseData.productName || caseData.verificationType || 'Address Verification';
  const userName = currentUser?.name || 'User';

  const defaultPhone = caseData.applicantPhone || '';
  const effectivePhone = targetPhone || defaultPhone;
  const cleanPhone = effectivePhone.replace(/[^0-9]/g, '');

  const summaryText = `🏢 AD VENTURES - CASE VERIFICATION
━━━━━━━━━━━━━━━━━━━━
📋 Case Ref: ${caseData.refNumber}
👤 Applicant: ${caseData.applicantName}
📞 Phone: ${caseData.applicantPhone || 'N/A'}
📍 Address: ${caseData.address || 'N/A'}
🏛️ Client: ${clientName}
🔍 Verification: ${productName}
📊 Status: ${(caseData.status || 'Active').toUpperCase()}
📅 Due Date: ${caseData.dueDate ? new Date(caseData.dueDate).toLocaleDateString('en-IN') : 'N/A'}

🔗 View Case Online:
${caseUrl}`;

  const whatsappMessage = `🏢 *AD VENTURES - CASE VERIFICATION*
━━━━━━━━━━━━━━━━━━━━
📋 *Case Ref:* ${caseData.refNumber}
👤 *Applicant:* ${caseData.applicantName}
📞 *Phone:* ${caseData.applicantPhone || 'N/A'}
📍 *Address:* ${caseData.address || 'N/A'}
🏛️ *Client:* ${clientName}
🔍 *Verification:* ${productName}
📊 *Status:* ${(caseData.status || 'Active').toUpperCase()}
📅 *Due Date:* ${caseData.dueDate ? new Date(caseData.dueDate).toLocaleDateString('en-IN') : 'N/A'}

🔗 *View Case Online:*
${caseUrl}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(caseUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
      toast.success('Case URL copied to clipboard!');

      logActivity({
        action: 'Case Link Copied',
        description: `${userName} copied shareable link for case ${caseData.refNumber}`,
        case_id: caseData.id,
        user_id: currentUserId,
      });
    } catch (err) {
      toast.error('Failed to copy URL');
    }
  };

  const handleCopySummary = async () => {
    try {
      await navigator.clipboard.writeText(summaryText);
      setCopiedSummary(true);
      setTimeout(() => setCopiedSummary(false), 2000);
      toast.success('Full case summary copied to clipboard!');

      logActivity({
        action: 'Case Summary Copied',
        description: `${userName} copied case text summary for ${caseData.refNumber}`,
        case_id: caseData.id,
        user_id: currentUserId,
      });
    } catch (err) {
      toast.error('Failed to copy summary');
    }
  };

  const handleWhatsAppShare = () => {
    const waUrl = cleanPhone
      ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodeURIComponent(whatsappMessage)}`
      : `https://api.whatsapp.com/send?text=${encodeURIComponent(whatsappMessage)}`;

    window.open(waUrl, '_blank', 'noopener,noreferrer');
    toast.success(`WhatsApp share opened for ${caseData.refNumber}!`);

    logActivity({
      action: 'WhatsApp Shared',
      description: `${userName} shared case ${caseData.refNumber} via WhatsApp${cleanPhone ? ` to ${effectivePhone}` : ''}`,
      case_id: caseData.id,
      user_id: currentUserId,
    });
  };

  const handleEmailShare = () => {
    const subject = `Case Verification Details - ${caseData.refNumber} (${caseData.applicantName})`;
    const mailtoUrl = `mailto:${caseData.applicantEmail || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(summaryText)}`;
    window.location.href = mailtoUrl;
    toast.success('Email draft opened with case details!');

    logActivity({
      action: 'Email Shared',
      description: `${userName} initiated email share for case ${caseData.refNumber} to ${caseData.applicantEmail || 'recipient'}`,
      case_id: caseData.id,
      user_id: currentUserId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white border-slate-200 shadow-xl rounded-xl">
        <DialogHeader className="pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600">
              <Share2 className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-base font-bold text-slate-900">
                Share Case Details
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Share case link, copy URL, or send formatted card via WhatsApp / Email
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Case Info Preview Card */}
        <div className="bg-slate-50 rounded-lg p-3.5 border border-slate-200 space-y-2 mt-2">
          <div className="flex items-center justify-between">
            <span className="font-mono font-bold text-sm text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
              {caseData.refNumber}
            </span>
            <Badge variant="outline" className="capitalize text-xs font-semibold">
              {caseData.status || 'Active'}
            </Badge>
          </div>

          <div className="text-xs text-slate-700 space-y-1">
            <div className="flex items-center gap-1.5 font-medium">
              <User className="w-3.5 h-3.5 text-slate-400" />
              <span>{caseData.applicantName}</span>
              {caseData.applicantPhone && (
                <span className="text-slate-400">({caseData.applicantPhone})</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-slate-500 truncate">
              <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="truncate">{clientName} • {productName}</span>
            </div>
            {caseData.address && (
              <div className="flex items-start gap-1.5 text-slate-500 line-clamp-1">
                <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                <span className="line-clamp-1">{caseData.address}</span>
              </div>
            )}
          </div>
        </div>

        {/* Shareable Link Input */}
        <div className="space-y-1.5 mt-2">
          <label className="text-xs font-semibold text-slate-700 flex items-center justify-between">
            <span>Shareable Case URL</span>
            <span className="text-[10px] text-slate-400">Direct access link</span>
          </label>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={caseUrl}
              className="font-mono text-xs bg-slate-50 border-slate-200 text-slate-600 select-all"
            />
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleCopyLink}
              className={`shrink-0 gap-1.5 font-semibold text-xs ${
                copiedLink ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {copiedLink ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  Copy Link
                </>
              )}
            </Button>
          </div>
        </div>

        {/* WhatsApp Custom Phone Option */}
        <div className="space-y-1.5 mt-1">
          <label className="text-xs font-semibold text-slate-700">
            WhatsApp Recipient Phone
          </label>
          <Input
            placeholder={caseData.applicantPhone || 'e.g. 9876543210'}
            value={targetPhone}
            onChange={(e) => setTargetPhone(e.target.value)}
            className="text-xs border-slate-200 placeholder:text-slate-400"
          />
        </div>

        {/* Share Action Buttons */}
        <div className="grid grid-cols-2 gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleWhatsAppShare}
            className="border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 font-semibold gap-2 justify-center text-xs h-9"
          >
            <MessageCircle className="w-4 h-4 text-emerald-600" />
            Share on WhatsApp
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleEmailShare}
            className="border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 font-semibold gap-2 justify-center text-xs h-9"
          >
            <Mail className="w-4 h-4 text-blue-600" />
            Share via Email
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleCopySummary}
            className="border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100 font-semibold gap-2 justify-center text-xs h-9"
          >
            {copiedSummary ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <FileText className="w-3.5 h-3.5 text-slate-500" />}
            {copiedSummary ? 'Summary Copied!' : 'Copy Summary'}
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={() => window.open(caseUrl, '_blank')}
            className="border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100 font-semibold gap-2 justify-center text-xs h-9"
          >
            <ExternalLink className="w-3.5 h-3.5 text-slate-500" />
            Open Case Page
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
